import { z } from 'zod';

// ── faktury zakupu z KSeF (Finanse → Zakupy) ────────────────────────────────

/** Praca księgowej z fakturą zakupu. */
export const PURCHASE_STATUSES = {
  nowa: 'Do sprawdzenia',
  zaakceptowana: 'Zaakceptowana',
  zaksiegowana: 'Zaksięgowana',
  odrzucona: 'Odrzucona',
} as const;
export type PurchaseStatus = keyof typeof PURCHASE_STATUSES;

export const PURCHASE_PAYMENT_STATUSES = { do_zaplaty: 'Do zapłaty', zaplacona: 'Zapłacona' } as const;
export type PurchasePaymentStatus = keyof typeof PURCHASE_PAYMENT_STATUSES;

/** RodzajFaktury w KSeF. */
export const KSEF_INVOICE_KINDS: Record<string, string> = {
  VAT: 'Faktura VAT',
  KOR: 'Faktura korygująca',
  ZAL: 'Faktura zaliczkowa',
  ROZ: 'Faktura rozliczeniowa',
  UPR: 'Faktura uproszczona',
  KOR_ZAL: 'Korekta faktury zaliczkowej',
  KOR_ROZ: 'Korekta faktury rozliczeniowej',
};

/** Środowiska KSeF. Testowe i przedprodukcyjne — dokumenty bez mocy prawnej. */
export const KSEF_MODES = {
  wylaczona: 'Wyłączona',
  test: 'Testowe (api-test)',
  demo: 'Przedprodukcyjne (demo)',
  produkcja: 'Produkcyjne',
} as const;
export type KsefMode = keyof typeof KSEF_MODES;

export const PURCHASE_FILTERS = {
  do_sprawdzenia: 'Do sprawdzenia',
  do_zaplaty: 'Do zapłaty',
  po_terminie: 'Po terminie',
  wszystkie: 'Wszystkie',
} as const;
export type PurchaseFilter = keyof typeof PURCHASE_FILTERS;

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const purchaseQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  companyId: z.string().uuid().optional(),
  filter: z.enum(Object.keys(PURCHASE_FILTERS) as [PurchaseFilter, ...PurchaseFilter[]]).default('wszystkie'),
  /** pokaż też odrzucone */
  rejected: z
    .string()
    .optional()
    .transform((v) => v === 'true' || v === '1'),
  from: day.optional(),
  to: day.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type PurchaseQuery = z.infer<typeof purchaseQuerySchema>;

export interface PurchaseRowDto {
  id: string;
  companyId: string;
  ksefNumber: string;
  mode: KsefMode;
  invoiceNumber: string;
  kind: string;
  issueDate: string | null;
  ksefReceivedAt: string | null;
  sellerNip: string;
  sellerName: string;
  currency: string;
  netGr: number | null;
  vatGr: number | null;
  grossGr: number | null;
  paymentDue: string | null;
  paymentMethod: string;
  status: PurchaseStatus;
  paymentStatus: PurchasePaymentStatus;
  paidAt: string | null;
  overdue: boolean;
  caseId: string | null;
  caseNumber: string | null;
  category: string;
  hasError: boolean;
}

export interface PurchaseListDto {
  items: PurchaseRowDto[];
  total: number;
  /** sumy całego wyniku (PLN; dokumenty w innych walutach — osobno w wierszach) */
  sums: { netGr: number; vatGr: number; grossGr: number; toPayGr: number; overdueGr: number };
  /** liczniki do zakładek (bez filtra rozliczenia) */
  counts: { doSprawdzenia: number; doZaplaty: number; poTerminie: number };
}

export interface PurchaseDetailDto extends PurchaseRowDto {
  saleDate: string | null;
  sellerAddress: string;
  bankAccount: string;
  paidInDocument: boolean;
  correctedKsefNumber: string | null;
  correctedNumber: string | null;
  vatLines: { rate: string; netGr: number; vatGr: number }[];
  items: { lp: number; name: string; unit: string; quantity: number | null; unitNetGr: number | null; netGr: number | null; grossGr: number | null; vatRate: string }[];
  statusNote: string;
  statusAt: string | null;
  statusByName: string | null;
  paidByName: string | null;
  note: string;
  parseError: string;
  /** inne dokumenty tego sprzedawcy (np. korekta ↔ faktura korygowana) */
  related: { id: string; invoiceNumber: string; kind: string; ksefNumber: string }[];
  can: { edit: boolean };
}

export const purchaseUpdateSchema = z.object({
  status: z.enum(Object.keys(PURCHASE_STATUSES) as [PurchaseStatus, ...PurchaseStatus[]]).optional(),
  statusNote: z.string().trim().max(500).optional(),
  caseId: z.string().uuid().nullable().optional(),
  category: z.string().trim().max(40).optional(),
  note: z.string().trim().max(2000).optional(),
});
export type PurchaseUpdateInput = z.infer<typeof purchaseUpdateSchema>;

export const purchasePaySchema = z.object({ paid: z.boolean(), paidAt: day.nullable().default(null) });
export type PurchasePayInput = z.infer<typeof purchasePaySchema>;

/** Kategorie kosztów (podpowiedzi; pole jest dowolne). */
export const PURCHASE_CATEGORIES = ['Materiały i urządzenia', 'Podwykonawcy', 'Paliwo i transport', 'Narzędzia i sprzęt', 'Media i telekomunikacja', 'Biuro i usługi', 'Inne'] as const;

// ── połączenie z KSeF ──

/** Sposób logowania do KSeF. */
export const KSEF_AUTH_METHODS = {
  token: 'Token KSeF',
  certyfikat: 'Certyfikat (podpis XAdES)',
} as const;
export type KsefAuthMethod = keyof typeof KSEF_AUTH_METHODS;

/** Jak KSeF rozpoznaje podmiot z certyfikatu. */
export const KSEF_SUBJECT_TYPES = {
  certificateSubject: 'Z danych certyfikatu (NIP / PESEL)',
  certificateFingerprint: 'Po odcisku palca (SHA-256) — gdy uprawnienia nadano na odcisk certyfikatu',
} as const;
export type KsefSubjectType = keyof typeof KSEF_SUBJECT_TYPES;

/** Zapisany certyfikat (bez klucza prywatnego — ten nie wraca do przeglądarki). */
export interface KsefCertificateDto {
  subject: string;
  issuer: string;
  serialNumber: string;
  validFrom: string;
  validTo: string;
  /** SHA-256, hex wielkimi literami */
  fingerprint: string;
  /** np. „RSA 2048”, „EC P-256” */
  keyType: string;
  /** identyfikator z certyfikatu: NIP / PESEL (albo pusty) */
  identifier: string;
  identifierType: 'NIP' | 'PESEL' | '';
  selfSigned: boolean;
  setAt: string | null;
}

/** Wgranie certyfikatu: certyfikat KSeF (.crt + .key) albo plik PKCS#12 (.p12 / .pfx). Pliki w base64. */
export const ksefCertificateUploadSchema = z.discriminatedUnion('format', [
  z.object({
    format: z.literal('pem'),
    certificate: z.string().min(10).max(40_000),
    privateKey: z.string().min(10).max(40_000),
    password: z.string().max(200).optional(),
  }),
  z.object({
    format: z.literal('p12'),
    file: z.string().min(10).max(60_000),
    password: z.string().max(200).optional(),
  }),
]);
export type KsefCertificateUploadInput = z.infer<typeof ksefCertificateUploadSchema>;

export interface KsefConnectionDto {
  companyId: string;
  nip: string;
  mode: KsefMode;
  auth: KsefAuthMethod;
  subjectType: KsefSubjectType;
  certificate: KsefCertificateDto | null;
  tokenHint: string;
  tokenSetAt: string | null;
  status: 'nieskonfigurowana' | 'ok' | 'blad';
  lastCheckAt: string | null;
  lastError: string;
  keyConfigured: boolean;
  /** pobieranie od dnia (pierwsze pobranie) */
  since: string | null;
  autoSync: boolean;
  /** dokąd pobrano (data przyjęcia do KSeF ostatniej faktury) */
  syncedTo: string | null;
  lastRun: { startedAt: string; finishedAt: string | null; found: number; added: number; error: string; trigger: 'auto' | 'reczne' } | null;
}

export const ksefConnectionUpdateSchema = z.object({
  mode: z.enum(Object.keys(KSEF_MODES) as [KsefMode, ...KsefMode[]]),
  auth: z.enum(Object.keys(KSEF_AUTH_METHODS) as [KsefAuthMethod, ...KsefAuthMethod[]]).default('token'),
  subjectType: z.enum(Object.keys(KSEF_SUBJECT_TYPES) as [KsefSubjectType, ...KsefSubjectType[]]).default('certificateSubject'),
  /** puste — bez zmiany tokenu */
  token: z.string().trim().max(1000).optional(),
  since: day.nullable().default(null),
  autoSync: z.boolean().default(true),
});
export type KsefConnectionUpdateInput = z.infer<typeof ksefConnectionUpdateSchema>;

export interface KsefSyncResultDto {
  found: number;
  added: number;
  error: string;
}

/** Stan KSeF wszystkich firm — pasek w Finansach → Zakupy. */
export interface KsefOverviewDto {
  companies: { companyId: string; mode: KsefMode; status: KsefConnectionDto['status']; lastRun: KsefConnectionDto['lastRun']; syncedTo: string | null }[];
  canSync: boolean;
  canConfigure: boolean;
}
